import React, { useState, useEffect } from 'react';
import { Plus, Layers, Pencil, Trash2 } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import DataGrid from '../../components/DataGrid';
import { useAuth } from '../../context/AuthContext';
import { useClientPagination } from '../../hooks/useClientPagination';
import { blocks as api, type Block, type ApiError } from '../../services/api';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};
const actionBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: '4px 6px', borderRadius: 6, color: 'var(--nx-text-muted)',
  display: 'inline-flex', alignItems: 'center',
};

const INIT = { name: '', floorCount: '' };

const BlocksPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [items, setItems]       = useState<Block[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState<Block | null>(null);
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm]         = useState(INIT);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmBlock, setConfirmBlock] = useState<Block | null>(null);
  const [deleteError, setDeleteError] = useState('');

  const { rows, page, setPage, totalPages } = useClientPagination(items, 20);

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  const change = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const openAdd = () => { setEditing(null); setFormError(''); setForm(INIT); setShowForm(true); };

  const openEdit = (b: Block) => {
    setEditing(b);
    setFormError('');
    setForm({ name: b.name, floorCount: b.floorCount != null ? String(b.floorCount) : '' });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); setForm(INIT); setFormError(''); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError('');
    const payload = {
      name:       form.name,
      floorCount: form.floorCount ? Number(form.floorCount) : undefined,
    };
    try {
      if (editing) {
        const updated = await api.update(societyId, editing.id, payload);
        setItems(p => p.map(b => b.id === updated.id ? updated : b));
      } else {
        const created = await api.create(societyId, payload);
        setItems(p => [created, ...p]);
      }
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to save block. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (b: Block) => { setConfirmBlock(b); setDeleteError(''); };

  const confirmDelete = async () => {
    if (!societyId || !confirmBlock) return;
    setDeletingId(confirmBlock.id);
    try {
      await api.delete(societyId, confirmBlock.id);
      setItems(p => p.filter(x => x.id !== confirmBlock.id));
      setConfirmBlock(null);
    } catch { setDeleteError('Failed to delete block. Please try again.'); }
    finally { setDeletingId(null); }
  };

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Blocks
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
            {items.length} block{items.length !== 1 ? 's' : ''} registered
          </p>
        </div>
        <button onClick={openAdd} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Add Block
        </button>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            {
              key: 'name', header: 'Block Name',
              cellStyle: { fontWeight: 500 },
              render: (b: Block) => b.name,
            },
            {
              key: 'floors', header: 'Floors',
              cellStyle: { color: 'var(--nx-text-secondary)' },
              render: (b: Block) => b.floorCount != null ? b.floorCount : '—',
            },
            {
              key: 'actions', header: '',
              cellStyle: { textAlign: 'right', whiteSpace: 'nowrap' },
              render: (b: Block) => (
                <>
                  <button onClick={() => openEdit(b)} style={actionBtn} title="Edit block">
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(b)}
                    disabled={deletingId === b.id}
                    style={{ ...actionBtn, color: '#EF4444', opacity: deletingId === b.id ? 0.4 : 1 }}
                    title="Delete block"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              ),
            },
          ]}
          rows={rows}
          rowKey={b => b.id}
          loading={loading}
          emptyIcon={<Layers size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No blocks yet. Add your first block to get started."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      <ConfirmDialog
        open={confirmBlock !== null}
        variant="danger"
        title={`Delete "${confirmBlock?.name}"?`}
        message="Units assigned to this block will lose their block association. This action cannot be undone."
        confirmLabel="Delete Block"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setConfirmBlock(null); setDeleteError(''); }}
      />

      {showForm && (
        <Modal
          title={editing ? 'Edit Block' : 'Add Block'}
          submitLabel={editing ? 'Save Changes' : 'Add Block'}
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Block Name *</label>
            <input name="name" value={form.name} onChange={change} required placeholder="e.g. Block A" style={inp} autoFocus />
          </div>
          <div>
            <label style={lbl}>Number of Floors</label>
            <input type="number" name="floorCount" value={form.floorCount} onChange={change} placeholder="e.g. 10" min="1" style={inp} />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default BlocksPage;
